import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
   Box,
   Container,
   Typography,
   Paper,
   TextField,
   Button,
   Grid,
   FormControl,
   InputLabel,
   Select,
   MenuItem,
   FormHelperText,
   FormControlLabel,
   Switch,
   Divider,
   Alert,
   CircularProgress,
   InputAdornment,
} from '@mui/material';
import {
   Event as EventIcon,
   LocationOn as LocationIcon,
   PhotoCamera as PhotoCameraIcon,
   Save as SaveIcon,
   ArrowBack as ArrowBackIcon,
   AutoAwesome as AutoAwesomeIcon,
   VideoCall as VideoCallIcon,
   Add as AddIcon,
   Refresh as RefreshIcon,
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { useEvents } from '../../contexts/EventContext';
import { EventType, Event } from '../../types/Event';
import {
   getStorage,
   ref,
   uploadBytesResumable,
   getDownloadURL,
   uploadBytes,
} from 'firebase/storage';
import { db } from '../../firebase/config';
import { Timestamp } from 'firebase/firestore';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DOMPurify from 'dompurify';
import AIImageGenerator from '../../components/EventForms/AIImageGenerator';
import CoverPhotoUpload from '../../components/EventForms/CoverPhotoUpload';
import GooglePlacesSearch from '../../components/common/GooglePlacesSearch';
import axios from 'axios';

// Define interface for Firestore document data that may contain Timestamps
interface FirebaseEvent {
   title?: string;
   description?: string;
   eventType?: EventType;
   location?: {
      name?: string;
      address?: string;
      coordinates?: number[];
   };
   startDate?: Date | Timestamp;
   endDate?: Date | Timestamp;
   isPublic?: boolean;
   coverImageUrl?: string;
   [key: string]: any;
}

// Event type options
const eventTypes: { value: EventType; label: string }[] = [
   { value: 'social', label: 'Social Gathering' },
   { value: 'family', label: 'Family Event' },
   { value: 'cultural', label: 'Cultural Event' },
   { value: 'sports', label: 'Sports Event' },
   { value: 'business', label: 'Business Event' },
   { value: 'other', label: 'Other' },
];

const DEFAULT_EVENT_IMAGE = '/assets/images/placeholder-event.jpg';

// Helper function to create a location object from string or existing location object
const createLocationObject = (locationInput: string, existingLocation?: any) => {
   // If existing location is an object, keep its structure but update name and address
   if (existingLocation && typeof existingLocation === 'object') {
      return {
         name: locationInput,
         address: locationInput,
         coordinates: existingLocation.coordinates || [0, 0],
      };
   }

   // Otherwise create a new location object
   return {
      name: locationInput,
      address: locationInput,
      coordinates: [0, 0],
   };
};

// Quill modules configuration
const quillModules = {
   toolbar: [
      [{ header: [1, 2, false] }],
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ align: [] }],
      [{ color: [] }],
      ['blockquote', 'link'],
      ['clean'],
   ],
   clipboard: {
      matchVisual: false,
   },
};

const quillFormats = [
   'header',
   'bold',
   'italic',
   'underline',
   'list',
   'bullet',
   'align',
   'color',
   'blockquote',
   'link',
];

// Add this function to clean HTML content
const cleanHtmlContent = (html: string): string => {
   if (!html) return '';

   // First, clean the HTML using DOMPurify with allowed tags and attributes
   let cleaned = DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
         'p',
         'br',
         'strong',
         'em',
         'u',
         'h1',
         'h2',
         'h3',
         'ul',
         'ol',
         'li',
         'blockquote',
         'a',
         'span',
      ],
      ALLOWED_ATTR: ['href', 'style'],
   });

   // Remove empty paragraphs that only contain &nbsp; or whitespace
   cleaned = cleaned.replace(/<p[^>]*>(\s|&nbsp;)*<\/p>/g, '');

   // Remove excessive line breaks while preserving intentional ones
   cleaned = cleaned.replace(/(\r\n|\n|\r){3,}/g, '\n\n');

   // Remove any style attributes that might have been added by the editor
   cleaned = cleaned.replace(/\s+style="[^"]*"/g, '');

   // Fix any double spaces
   cleaned = cleaned.replace(/\s{2,}/g, ' ');

   // Ensure proper spacing around list items
   cleaned = cleaned.replace(/<\/(ul|ol)><(ul|ol)>/g, '</\$1>\n<\$2>');

   // Clean up spaces around HTML tags
   cleaned = cleaned.replace(/>\s+</g, '><');

   return cleaned.trim();
};

const EditEvent: React.FC = () => {
   const { eventId } = useParams<{ eventId: string }>();
   const navigate = useNavigate();
   const { getEvent, updateEvent } = useEvents();

   // Move all hooks before any conditional returns
   const [event, setEvent] = useState<Event | null>(null);
   const [loading, setLoading] = useState(true);
   const [saving, setSaving] = useState(false);
   const [error, setError] = useState<string | null>(null);

   // Form state
   const [title, setTitle] = useState('');
   const [description, setDescription] = useState('');
   const [eventType, setEventType] = useState<EventType>('social');
   const [location, setLocation] = useState<any>(null);
   const [startDate, setStartDate] = useState<Date | null>(new Date());
   const [endDate, setEndDate] = useState<Date | null>(new Date());
   const [isPublic, setIsPublic] = useState(true);

   // Virtual event state
   const [isVirtual, setIsVirtual] = useState(false);
   const [virtualRoomSid, setVirtualRoomSid] = useState<string | null>(null);
   const [virtualRoomName, setVirtualRoomName] = useState<string | null>(null);
   const [virtualRoomUrl, setVirtualRoomUrl] = useState<string | null>(null);
   const [virtualRoomStatus, setVirtualRoomStatus] = useState<string | null>(null);
   const [creatingVirtualRoom, setCreatingVirtualRoom] = useState(false);

   // Cover photo state
   const [coverPhotoUrl, setCoverPhotoUrl] = useState<string | null>(null);
   const [coverPhotoFile, setCoverPhotoFile] = useState<File | null>(null);

   // AI generator state
   const [showAIGenerator, setShowAIGenerator] = useState(false);

   useEffect(() => {
      fetchEventDetails();
   }, [eventId]);

   // Now we can safely do the conditional check
   if (!eventId) {
      navigate('/events');
      return null;
   }

   const fetchEventDetails = async () => {
      if (!eventId) return;

      try {
         setLoading(true);
         setError(null);

         const eventData = await getEvent(eventId);

         if (!eventData) {
            setError('Event not found');
            setLoading(false);
            return;
         }

         setEvent(eventData);

         // Set form values
         setTitle(eventData.title || '');
         setDescription(eventData.description || '');
         setEventType((eventData.eventType as EventType) || 'social');
         setLocation(eventData?.location || null);

         // Handle dates properly with Timestamp objects
         if (eventData.startDate) {
            // Firebase's Firestore returns either a Timestamp object or a regular Date
            if (typeof eventData.startDate === 'object' && 'toDate' in eventData.startDate) {
               // First cast to unknown then to Timestamp to avoid type errors
               const timestamp = eventData.startDate as unknown as Timestamp;
               setStartDate(timestamp.toDate());
            } else {
               setStartDate(new Date(eventData.startDate as any));
            }
         }

         if (eventData.endDate) {
            // Firebase's Firestore returns either a Timestamp object or a regular Date
            if (typeof eventData.endDate === 'object' && 'toDate' in eventData.endDate) {
               // First cast to unknown then to Timestamp to avoid type errors
               const timestamp = eventData.endDate as unknown as Timestamp;
               setEndDate(timestamp.toDate());
            } else {
               setEndDate(new Date(eventData.endDate as any));
            }
         }

         setIsPublic(eventData.isPublic !== false);

         // Set virtual event data if it exists
         setIsVirtual(eventData.isVirtual || false);
         setVirtualRoomSid(eventData.virtualRoomSid || null);
         setVirtualRoomName(eventData.virtualRoomName || null);
         setVirtualRoomUrl(eventData.virtualRoomUrl || null);
         setVirtualRoomStatus(eventData.virtualRoomStatus || null);

         // Set cover photo url if it exists
         if (eventData.coverImageUrl) {
            setCoverPhotoUrl(eventData.coverImageUrl);
         }

         setLoading(false);
      } catch (err) {
         console.error('Error fetching event details:', err);
         setError('Failed to load event details. Please try again.');
         setLoading(false);
      }
   };

   const handleCoverPhotoChange = (url: string | null, file: File | null) => {
      setCoverPhotoUrl(url);
      setCoverPhotoFile(file);
   };

   const handleAIImageGenerated = (file: File | null, previewUrl: string | null) => {
      setCoverPhotoFile(file);
      setCoverPhotoUrl(previewUrl);

      setShowAIGenerator(false);
   };

   const createVirtualRoom = async () => {
      if (!eventId || !title) {
         setError('Event title is required to create virtual room');
         return;
      }

      try {
         setCreatingVirtualRoom(true);
         setError(null);

         // Create Twilio room
         const roomResponse = await axios.post('/api/twilio/create-room', {
            roomName: `${title}-${eventId}`,
            roomType: 'group',
            maxParticipants: 50,
            recordParticipantsOnConnect: false,
         });

         if (roomResponse.data.success) {
            const roomData = roomResponse.data;

            // Update local state
            setIsVirtual(true);
            setVirtualRoomSid(roomData.roomSid);
            setVirtualRoomName(roomData.roomName);
            setVirtualRoomUrl(roomData.url);
            setVirtualRoomStatus(roomData.status);

            // Update the event in the database
            const eventData = {
               isVirtual: true,
               virtualRoomSid: roomData.roomSid,
               virtualRoomName: roomData.roomName,
               virtualRoomUrl: roomData.url,
               virtualRoomStatus: roomData.status,
               virtualEventProvider: 'twilio',
            };

            await updateEvent(eventId, eventData);

            console.log('Virtual room created successfully:', roomData);
         } else {
            throw new Error(roomResponse.data.error || 'Failed to create virtual room');
         }
      } catch (err: any) {
         console.error('Error creating virtual room:', err);
         setError(err.response?.data?.error || err.message || 'Failed to create virtual room');
      } finally {
         setCreatingVirtualRoom(false);
      }
   };

   const handleUpload = async (uploadFile: File) => {
      if (!uploadFile) {
         return;
      }

      try {
         const storage = getStorage();
         const fileExtension = uploadFile.name.split('.').pop()?.toLowerCase() || 'jpg';
         const safeFileName = `${eventId}_${Date.now()}.${fileExtension}`;
         const storagePath = `events/covers/${safeFileName}`;
         const storageRef = ref(storage, storagePath);

         const metadata = {
            contentType: uploadFile.type,
            customMetadata: {
               originalName: uploadFile.name,
               uploadedBy: 'event_editor',
               entityId: eventId,
            },
         };

         await uploadBytes(storageRef, uploadFile, metadata);
         const downloadUrl = await getDownloadURL(storageRef);
         return downloadUrl;
      } catch (err: any) {
         throw err;
      }
   };

   const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();

      if (!eventId) return;

      try {
         setSaving(true);
         setError(null);

         // Prepare event data
         const eventData: any = {
            title,
            description: cleanHtmlContent(description),
            eventType,
            isPublic,
            location: location,
            startDate,
            endDate,
            isVirtual,
            virtualRoomSid,
            virtualRoomName,
            virtualRoomUrl,
            virtualRoomStatus,
            virtualEventProvider: isVirtual ? 'twilio' : null,
         };

         if (coverPhotoFile) {
            const coverPhotoUrl = await handleUpload(coverPhotoFile);
            eventData.coverImageUrl = coverPhotoUrl;
         }

         // Update the event
         await updateEvent(eventId, eventData);

         setSaving(false);
         navigate(`/events/${eventId}`);
      } catch (err: any) {
         console.error('Error updating event:', err);
         setError(err.message || 'Failed to update event. Please try again.');
         setSaving(false);
      }
   };

   if (loading) {
      return (
         <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
         </Box>
      );
   }

   if (error) {
      return (
         <Container maxWidth='md'>
            <Alert severity='error' sx={{ mt: 4 }}>
               {error}
            </Alert>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
               <Button
                  variant='contained'
                  onClick={() => navigate(`/events/${eventId}`)}
                  startIcon={<ArrowBackIcon />}
               >
                  Back to Event
               </Button>
            </Box>
         </Container>
      );
   }

   return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
         <Container maxWidth='md' sx={{ py: 4 }}>
            <Paper sx={{ p: 3, borderRadius: 2 }}>
               <Box sx={{ mb: 4 }}>
                  <Typography variant='h4' component='h1' gutterBottom>
                     Edit Event
                  </Typography>
                  <Typography variant='body1' color='text.secondary'>
                     Update your event details and cover photo
                  </Typography>
               </Box>

               {error && (
                  <Alert severity='error' sx={{ mb: 3 }}>
                     {error}
                  </Alert>
               )}

               <form onSubmit={handleSubmit}>
                  {/* Basic Information */}
                  <Typography variant='h6' sx={{ mt: 3, mb: 2 }} component='h2'>
                     <EventIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                     Basic Information
                  </Typography>
                  <Grid container spacing={3}>
                     <Grid item xs={12}>
                        <TextField
                           fullWidth
                           label='Event Title'
                           value={title}
                           onChange={(e) => setTitle(e.target.value)}
                           required
                        />
                     </Grid>

                     <Grid item xs={12}>
                        <Typography variant='subtitle1' gutterBottom>
                           Event Description
                        </Typography>
                        <Box
                           sx={{
                              '.ql-container': {
                                 minHeight: '300px',
                                 fontSize: '1rem',
                              },
                              '.ql-editor': {
                                 minHeight: '300px',
                              },
                              '.ql-editor h1': {
                                 fontSize: '1.5rem',
                                 fontWeight: 'bold',
                                 marginBottom: '1rem',
                              },
                              '.ql-editor h2': {
                                 fontSize: '1.25rem',
                                 fontWeight: 'bold',
                                 marginBottom: '0.75rem',
                              },
                              '.ql-editor ul, .ql-editor ol': {
                                 paddingLeft: '2rem',
                                 marginBottom: '1rem',
                              },
                              '.ql-editor li': {
                                 marginBottom: '0.5rem',
                              },
                              '.ql-editor p': {
                                 marginBottom: '1rem',
                                 lineHeight: '1.6',
                              },
                           }}
                        >
                           <ReactQuill
                              value={description}
                              onChange={(content) => setDescription(content)}
                              modules={quillModules}
                              formats={quillFormats}
                              placeholder='Create an engaging description that sells your event...'
                           />
                        </Box>
                     </Grid>

                     <Grid item xs={12} sm={6}>
                        <FormControl fullWidth required>
                           <InputLabel>Event Type</InputLabel>
                           <Select
                              value={eventType}
                              label='Event Type'
                              onChange={(e) => setEventType(e.target.value as EventType)}
                           >
                              {eventTypes.map((type) => (
                                 <MenuItem key={type.value} value={type.value}>
                                    {type.label}
                                 </MenuItem>
                              ))}
                           </Select>
                        </FormControl>
                     </Grid>

                     <Grid item xs={12} sm={6}>
                        <FormControlLabel
                           control={
                              <Switch
                                 checked={isPublic}
                                 onChange={(e) => setIsPublic(e.target.checked)}
                              />
                           }
                           label='Public Event (visible to everyone)'
                        />
                     </Grid>
                  </Grid>

                  {/* Cover Photo Upload */}
                  <Box sx={{ mt: 4, mb: 3 }}>
                     <CoverPhotoUpload
                        initialImageUrl={event?.coverImageUrl}
                        onChange={handleCoverPhotoChange}
                        entityId={eventId}
                        folder='events/covers'
                        eventTitle={title}
                        eventDescription={description}
                        eventType={eventType}
                     />
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  {/* Location & Date */}
                  <Typography variant='h6' sx={{ mt: 3, mb: 2 }} component='h2'>
                     <LocationIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                     Location & Date
                  </Typography>
                  <Grid container spacing={3}>
                     <Grid item xs={12}>
                        {/* <TextField
                  fullWidth
                  label="Event Location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationIcon />
                      </InputAdornment>
                    ),
                  }}
                /> */}
                        <GooglePlacesSearch
                           sx={{ mb: 2 }}
                           value={location?.name || ''}
                           searchType='places'
                           label='Location'
                           onPlaceSelect={(place_name: any, lat: any, lng: any) => {
                              setLocation({
                                 name: place_name,
                                 address: place_name,
                                 coordinates: [lat, lng],
                              });
                           }}
                        />
                     </Grid>

                     <Grid item xs={12} sm={6}>
                        <DateTimePicker
                           label='Start Date & Time'
                           value={startDate}
                           onChange={(date) => setStartDate(date)}
                           renderInput={(params) => <TextField {...params} fullWidth required />}
                        />
                     </Grid>

                     <Grid item xs={12} sm={6}>
                        <DateTimePicker
                           label='End Date & Time'
                           value={endDate}
                           onChange={(date) => setEndDate(date)}
                           renderInput={(params) => <TextField {...params} fullWidth required />}
                        />
                     </Grid>
                  </Grid>

                  {/* Virtual Event Section - Only show if event is virtual */}
                  {isVirtual && (
                     <>
                        <Divider sx={{ my: 3 }} />

                        <Typography variant='h6' sx={{ mt: 3, mb: 2 }} component='h2'>
                           <VideoCallIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                           Virtual Event
                        </Typography>

                        {virtualRoomSid ? (
                           <Box sx={{ mb: 3 }}>
                              <Alert severity='success' sx={{ mb: 2 }}>
                                 <Typography variant='subtitle1' gutterBottom>
                                    Virtual Event Room Created
                                 </Typography>
                                 <Typography variant='body2' sx={{ mb: 1 }}>
                                    <strong>Room ID:</strong> {virtualRoomSid}
                                 </Typography>
                                 {virtualRoomName && (
                                    <Typography variant='body2' sx={{ mb: 1 }}>
                                       <strong>Room Name:</strong> {virtualRoomName}
                                    </Typography>
                                 )}
                                 {virtualRoomStatus && (
                                    <Typography variant='body2' sx={{ mb: 1 }}>
                                       <strong>Status:</strong> {virtualRoomStatus}
                                    </Typography>
                                 )}
                              </Alert>

                              <Button
                                 variant='outlined'
                                 onClick={createVirtualRoom}
                                 disabled={creatingVirtualRoom}
                                 startIcon={
                                    creatingVirtualRoom ? (
                                       <CircularProgress size={20} />
                                    ) : (
                                       <RefreshIcon />
                                    )
                                 }
                                 sx={{ mr: 2 }}
                              >
                                 {creatingVirtualRoom ? 'Recreating...' : 'Recreate Virtual Room'}
                              </Button>
                           </Box>
                        ) : (
                           <Box sx={{ mb: 3 }}>
                              <Alert severity='info' sx={{ mb: 2 }}>
                                 <Typography variant='body2'>
                                    This virtual event doesn't have a meeting room yet. Click the
                                    button below to create a Twilio virtual event room for online
                                    participation.
                                 </Typography>
                              </Alert>

                              <Button
                                 variant='contained'
                                 onClick={createVirtualRoom}
                                 disabled={creatingVirtualRoom || !title}
                                 startIcon={
                                    creatingVirtualRoom ? (
                                       <CircularProgress size={20} />
                                    ) : (
                                       <AddIcon />
                                    )
                                 }
                              >
                                 {creatingVirtualRoom
                                    ? 'Creating Virtual Room...'
                                    : 'Create Virtual Event Room'}
                              </Button>

                              {!title && (
                                 <Typography
                                    variant='caption'
                                    display='block'
                                    sx={{ mt: 1, color: 'text.secondary' }}
                                 >
                                    Please enter an event title first
                                 </Typography>
                              )}
                           </Box>
                        )}
                     </>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                     <Button
                        variant='outlined'
                        onClick={() => navigate(`/events/${eventId}`)}
                        startIcon={<ArrowBackIcon />}
                     >
                        Cancel
                     </Button>

                     <Button
                        variant='contained'
                        type='submit'
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                     >
                        {saving ? 'Saving...' : 'Save Changes'}
                     </Button>
                  </Box>
               </form>
            </Paper>
         </Container>
         <AIImageGenerator
            open={showAIGenerator}
            onClose={() => setShowAIGenerator(false)}
            eventTitle={title || 'Event'}
            eventDescription={description || ''}
            eventType={eventType || ''}
            onImageGenerated={handleAIImageGenerated}
         />
      </LocalizationProvider>
   );
};

export default EditEvent;
